// Skeleton setup for LibrarySettingsSection based on EmailToolsSection structure

import {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
} from "../../../components/ui/card"
import {
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
} from "../../../components/ui/tabs"

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import { Checkbox } from "../../../components/ui/checkbox"
import { Label } from "../../../components/ui/label"
import { RefreshCw, Plus, Edit, Trash, EyeOff, Eye } from "lucide-react"
import { useState, useEffect } from "react"
import { updateLibraryItem } from "../../../api/adminService"
import { Toast } from "../../../components/Toast"


const ITEMS_PER_PAGE = 5

export function LibrarySettingsSection({ libraryItems = [], setLibraryItems, supplementaryItems = [], handleSync, statusMessage, isSyncing, onUpdateSupplementary }: any) {
    const [selectedLibraryItem, setSelectedLibraryItem] = useState<any | null>(null);
    const [formData, setFormData] = useState<any>({});
    const [activeType, setActiveType] = useState("all")
    const [includeSupplementary, setIncludeSupplementary] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [expandedItems, setExpandedItems] = useState<string[]>([])
    const [editingItem, setEditingItem] = useState<any>(null)
    const [newSupplementary, setNewSupplementary] = useState({
        title: "",
        description: "",
        link: "",
        type: "book"
    })

    const libItems: any = libraryItems

    // Generate stats dynamically
    const libraryStats = libItems.reduce((acc: any, item: any) => {
        const type = item.library_type || "unknown"
        acc[type] = (acc[type] || 0) + 1
        return acc
    }, { all: libItems.length })

    const filteredItems = activeType === "all"
        ? libItems
        : libItems.filter((i: any) => i.library_type === activeType)

    const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE)
    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    )

    const toggleExpand = (id: string) => {
        setExpandedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        )
    }

    const handleUpdate = async () => {
        try {
            const payload = {
                ...formData,
                isManualUpdate: true,
                study_notes: {
                    visible: formData.study_notes?.visible || false,
                    content: formData.study_notes?.content || "",
                    signed: formData.study_notes?.signed || false,
                },
            };
            await updateLibraryItem(payload);

            setLibraryItems((prev: any) =>
                prev.map((item: any) => (item.id === payload.id ? payload : item))
            );
            setSelectedLibraryItem(null);
            Toast.success("Library item updated successfully");
        } catch (err) {
            Toast.error("Update failed: " + err);
        }
    };

    const handlleVisibleorDeleted = (item: any, field: "visible" | "deleted") => {
        const current = item[field];

        const toggledValue = current === "true" || current === true ? "false" : "true";

        const updatedItem = {
            ...item,
            [field]: toggledValue,
            isManualUpdate: true,
        };

        updateLibraryItem(updatedItem)
            .then(() => {
                setLibraryItems((prev:any) =>
                    prev.map((itm:any) => (itm.id === item.id ? updatedItem : itm))
                );
                Toast.success(`${field.charAt(0).toUpperCase() + field.slice(1)} updated successfully`);
            })
            .catch((err) => {
                Toast.error(`Failed to update ${field}: ${err}`);
            });
    };

    return (
        <div className="space-y-6">
            {/* Library Card */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">📚 Library Settings</CardTitle>
                    <CardDescription>Manage YouTube content and supplementary resources</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Stats */}
                    {/* <p>{JSON.stringify(libraryItems)}</p> */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(libraryStats).map(([type, count]: any) => (
                            <Button
                                key={type}
                                variant={activeType === type ? "default" : "outline"}
                                onClick={() => {
                                    setCurrentPage(1)
                                    setActiveType(type)
                                }}
                            >
                                {type.charAt(0).toUpperCase() + type.slice(1)} ({count})
                            </Button>
                        ))}
                    </div>

                    {/* Items */}
                    <div className="space-y-4">
                        {paginatedItems.map((item: any) => (
                            <div key={item.id} className="border rounded p-4 space-y-2 bg-muted/10">
                                <div className="flex justify-between items-center">
                                    <h4 className="font-medium">{item.title}</h4>
                                    <div className="flex gap-2">
                                        <Button variant="ghost" size="sm" onClick={() => toggleExpand(item.id)}>
                                            {expandedItems.includes(item.id) ? "Collapse" : "Expand"}
                                        </Button>

                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                handlleVisibleorDeleted(item, "visible");
                                            }}
                                        >
                                            {item.visible === "true" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </Button>

                                        <Dialog open={!!selectedLibraryItem} onOpenChange={(isOpen) => {
                                            if (!isOpen) {
                                                setSelectedLibraryItem(null); // Close dialog when clicked outside or on close
                                            }
                                        }}>
                                            <DialogTrigger asChild>
                                                <Button variant="ghost" size="sm"
                                                    onClick={() => {
                                                        setSelectedLibraryItem(item);
                                                        setFormData({ ...item });
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-2xl h-[90%]">
                                                <DialogHeader>
                                                    <DialogTitle>Edit Library Item</DialogTitle>
                                                    <DialogDescription>Update the information and save changes.</DialogDescription>
                                                </DialogHeader>

                                                {selectedLibraryItem && (
                                                    <div className="space-y-4 h-[100%] overflow-y-auto">
                                                        {/* Title */}
                                                        <div>
                                                            <label className="text-sm font-medium">Title</label>
                                                            <Input
                                                                value={formData.title}
                                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* YouTube ID */}
                                                        <div>
                                                            <label className="text-sm font-medium">YouTube ID</label>
                                                            <Input
                                                                value={formData.youtube_id}
                                                                onChange={(e) => setFormData({ ...formData, youtube_id: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* YouTube URL */}
                                                        <div>
                                                            <label className="text-sm font-medium">YouTube URL</label>
                                                            <Input
                                                                value={formData.youtube_url}
                                                                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Thumbnail */}
                                                        <div>
                                                            <label className="text-sm font-medium">Thumbnail URL</label>
                                                            <Input
                                                                value={formData.thumbnail}
                                                                onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Date */}
                                                        <div>
                                                            <label className="text-sm font-medium">Date</label>
                                                            <Input
                                                                type="datetime-local"
                                                                value={formData.date ? formData.date.slice(0, 16) : ""}
                                                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Duration */}
                                                        <div>
                                                            <label className="text-sm font-medium">Duration</label>
                                                            <Input
                                                                value={formData.duration}
                                                                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Speaker */}
                                                        <div>
                                                            <label className="text-sm font-medium">Speaker</label>
                                                            <Input
                                                                value={formData.speaker}
                                                                onChange={(e) => setFormData({ ...formData, speaker: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Views */}
                                                        <div>
                                                            <label className="text-sm font-medium">Views</label>
                                                            <Input
                                                                type="number"
                                                                value={formData.views}
                                                                onChange={(e) => setFormData({ ...formData, views: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Likes */}
                                                        <div>
                                                            <label className="text-sm font-medium">Likes</label>
                                                            <Input
                                                                type="number"
                                                                value={formData.likes}
                                                                onChange={(e) => setFormData({ ...formData, likes: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Description */}
                                                        <div>
                                                            <label className="text-sm font-medium">Description</label>
                                                            <Textarea
                                                                value={formData.description}
                                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                            />
                                                        </div>

                                                        {/* Format */}
                                                        <div>
                                                            <label className="text-sm font-medium">Format</label>
                                                            <select
                                                                className="w-full rounded border p-2"
                                                                value={formData.format}
                                                                onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                                                            >
                                                                <option value="">Select format</option>
                                                                <option value="video">Video</option>
                                                                <option value="audio">Audio</option>
                                                            </select>
                                                        </div>

                                                        {/* Library Type */}
                                                        <div>
                                                            <label className="text-sm font-medium">Library Type</label>
                                                            <select
                                                                className="w-full rounded border p-2"
                                                                value={formData.library_type}
                                                                onChange={(e) => setFormData({ ...formData, library_type: e.target.value })}
                                                            >
                                                                <option value="">Select type</option>
                                                                <option value="live">Live</option>
                                                                <option value="event">Event</option>
                                                                <option value="bst">Bible Study</option>
                                                            </select>
                                                        </div>


                                                        {/* Study Notes */}
                                                        <div className="border-t pt-4 mt-4 space-y-2">
                                                            <h4 className="text-sm font-semibold">Study Notes</h4>
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.study_notes?.visible || false}
                                                                    onChange={(e) =>
                                                                        setFormData({
                                                                            ...formData,
                                                                            study_notes: {
                                                                                ...formData.study_notes,
                                                                                visible: e.target.checked,
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                                <label className="text-sm">Visible</label>
                                                            </div>
                                                            <div>
                                                                <label className="text-sm font-medium">Content</label>
                                                                <Textarea
                                                                    value={formData.study_notes?.content || ""}
                                                                    onChange={(e) =>
                                                                        setFormData({
                                                                            ...formData,
                                                                            study_notes: {
                                                                                ...formData.study_notes,
                                                                                content: e.target.value,
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={formData.study_notes?.signed || false}
                                                                    onChange={(e) =>
                                                                        setFormData({
                                                                            ...formData,
                                                                            study_notes: {
                                                                                ...formData.study_notes,
                                                                                signed: e.target.checked,
                                                                            },
                                                                        })
                                                                    }
                                                                />
                                                                <label className="text-sm">Signed</label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}


                                                <div className="pt-4 flex justify-end gap-2">
                                                    <Button variant="outline" onClick={() => setSelectedLibraryItem(null)}>
                                                        Cancel
                                                    </Button>
                                                    <Button onClick={handleUpdate}>Update</Button>
                                                </div>
                                            </DialogContent>
                                        </Dialog>

                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                handlleVisibleorDeleted(item, "deleted");
                                            }}
                                        >
                                            <Trash className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                {expandedItems.includes(item.id) && (
                                    <div className="text-sm text-muted-foreground space-y-1">
                                        <p><strong>YouTube ID:</strong> {item.youtube_id}</p>
                                        <p><strong>Speaker:</strong> {item.speaker}</p>
                                        <p><strong>Duration:</strong> {item.duration}</p>
                                        <p><strong>Date:</strong> {item.date}</p>
                                        <p><strong>Description:</strong> {item.description}</p>
                                        <p><strong>Notes:</strong> {item.studyNotes?.content || "No notes"}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between pt-4">
                        <p className="text-sm text-muted-foreground">
                            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                            {Math.min(currentPage * ITEMS_PER_PAGE, filteredItems.length)} of {filteredItems.length}
                        </p>
                        <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</Button>
                            <Button size="sm" variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}>Prev</Button>
                            {[...Array(totalPages)].map((_, idx) => {
                                const page = idx + 1
                                return (
                                    <Button
                                        key={page}
                                        variant={currentPage === page ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </Button>
                                )
                            }).slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))}
                            <Button size="sm" variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}>Next</Button>
                            <Button size="sm" variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</Button>
                        </div>
                    </div>

                    {/* Sync */}
                    <div className="flex justify-between pt-6">
                        <div className="flex items-center gap-2">
                            <Checkbox id="include-supp" checked={includeSupplementary} onCheckedChange={() => setIncludeSupplementary(!includeSupplementary)} />
                            <Label htmlFor="include-supp">Include Supplementary Resources</Label>
                        </div>
                        {statusMessage && (
                            <div className="text-sm text-muted-foreground ml-4 whitespace-nowrap">
                                {statusMessage}
                            </div>
                        )}
                        <Button onClick={() => handleSync(includeSupplementary)} disabled={isSyncing}>
                            <RefreshCw className="h-4 w-4 mr-2" /> Sync YouTube Content
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Supplementary */}
            <Card>
                <CardHeader>
                    <CardTitle>📦 Supplementary Resources</CardTitle>
                    <CardDescription>Add, edit, or remove other resources like books and articles</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-6 lg:grid-cols-2">
                    <div className="space-y-2">
                        <Input placeholder="Title" value={newSupplementary.title} onChange={(e) => setNewSupplementary({ ...newSupplementary, title: e.target.value })} />
                        <Textarea placeholder="Description" value={newSupplementary.description} onChange={(e) => setNewSupplementary({ ...newSupplementary, description: e.target.value })} />
                        <Input placeholder="Link" value={newSupplementary.link} onChange={(e) => setNewSupplementary({ ...newSupplementary, link: e.target.value })} />
                        <Input placeholder="Type (e.g. book, article)" value={newSupplementary.type} onChange={(e) => setNewSupplementary({ ...newSupplementary, type: e.target.value })} />
                        <Button onClick={() => onUpdateSupplementary(newSupplementary)}>
                            <Plus className="h-4 w-4 mr-2" /> Add Resource
                        </Button>
                    </div>

                    <div className="space-y-2">
                        {supplementaryItems.map((item: any) => (
                            <div key={item.id} className="flex justify-between items-center border-b pb-2">
                                <div>
                                    <p className="font-medium">{item.title}</p>
                                    <p className="text-sm text-muted-foreground">{item.type} – {item.link}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button variant="ghost" size="sm" onClick={() => setEditingItem(item)}>
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Edit Resource</DialogTitle>
                                                <DialogDescription>Modify the supplementary resource info</DialogDescription>
                                            </DialogHeader>
                                            {editingItem && (
                                                <div className="space-y-3">
                                                    <Input value={editingItem.title} onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })} />
                                                    <Textarea value={editingItem.description} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} />
                                                    <Input value={editingItem.link} onChange={(e) => setEditingItem({ ...editingItem, link: e.target.value })} />
                                                    <Input value={editingItem.type} onChange={(e) => setEditingItem({ ...editingItem, type: e.target.value })} />
                                                    <Button onClick={() => onUpdateSupplementary(editingItem)}>Save Changes</Button>
                                                </div>
                                            )}
                                        </DialogContent>
                                    </Dialog>
                                    <Button variant="ghost" size="sm" onClick={() => onUpdateSupplementary({ ...item, delete: true })}>
                                        <Trash className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

